import { useMemo } from 'react';
import useVersionManagementV1 from './useVersionManagementV1';

/**
 * Custom hook to manage process flow versions
 * Stores and tracks different versions of process flows from regenerations
 */
const useProcessFlowVersions = (conversation, isStreaming, streamingContent, isLocalUpdateRef = null) => {
  // Extract Mermaid diagrams from markdown content
  const extractMermaidDiagrams = (markdown) => {
    const diagrams = [];

    if (!markdown) return diagrams;

    // Look for ```mermaid blocks
    const regex = /```mermaid\s*([\s\S]*?)```/g;
    const titleRegex = /#+\s*(.*?)(?:\n|$)/g;

    const hasMermaidBlocks = markdown.includes('```mermaid');

    if (!hasMermaidBlocks) {
      return diagrams;
    }

    let match;
    const titles = [];

    // First, find all titles
    while ((match = titleRegex.exec(markdown)) !== null) {
      titles.push({
        title: match[1].trim(),
        index: match.index
      });
    }

    // Then find all Mermaid diagrams
    const diagramRegex = /```mermaid\s*([\s\S]*?)```/g;
    let diagramCount = 0;

    while ((match = diagramRegex.exec(markdown)) !== null) {
      diagramCount++;

      // Find the most recent title for this diagram
      const currentTitle = titles
        .filter(t => t.index < match.index)
        .slice(-1)[0]?.title || `Flow ${diagrams.length + 1}`;

      // Get the description between the title and the diagram
      const titleIndex = titles.findIndex(t => t.title === currentTitle);
      let descriptionBefore = '';
      let descriptionAfter = '';
      let keySteps = [];

      if (titleIndex >= 0 && titleIndex < titles.length) {
        const titleEndIndex = titles[titleIndex].index + currentTitle.length;
        const nextElement = match.index;

        if (nextElement > titleEndIndex) {
          descriptionBefore = markdown.substring(titleEndIndex, match.index).trim();
          descriptionBefore = descriptionBefore.replace(/#+/g, '').trim();
          descriptionBefore = descriptionBefore.replace(/^[^:]*:[^:]*(?=\s*This diagram)/i, '');
          descriptionBefore = descriptionBefore.replace(/\n+/g, ' ');
          descriptionBefore = descriptionBefore.replace(/\*\*/g, '');
          descriptionBefore = descriptionBefore.replace(/\*/g, '');
          descriptionBefore = descriptionBefore.replace(/^[:;]+\s*/, '');
          descriptionBefore = descriptionBefore.replace(/^\w+\s+/, '');
        }
      }

      // Check for content after the diagram
      if (match.index + match[0].length < markdown.length) {
        const diagramEndIndex = match.index + match[0].length;

        const nextTitleIndex = titles.findIndex(t => t.index > diagramEndIndex);
        const nextTitle = nextTitleIndex >= 0 ? titles[nextTitleIndex].index : markdown.length;

        const nextDiagramRegex = /```mermaid\s*([\s\S]*?)```/g;
        const nextDiagramMatch = nextDiagramRegex.exec(markdown.substring(diagramEndIndex));
        const nextDiagram = nextDiagramMatch ? diagramEndIndex + nextDiagramMatch.index : markdown.length;

        const endOfSection = Math.min(nextTitle, nextDiagram);

        if (endOfSection > diagramEndIndex) {
          const afterText = markdown.substring(diagramEndIndex, endOfSection).trim();

          if (afterText && !afterText.startsWith('#')) {
            if (afterText.includes('**Key Steps:**')) {
              const keyStepsSection = afterText.split('**Key Steps:**')[1].trim();
              const bulletPoints = keyStepsSection.split(/\n\s*-\s+/).filter(item => item.trim());

              if (bulletPoints.length > 0) {
                keySteps = bulletPoints.map(step =>
                  step.replace(/\*\*/g, '')
                      .replace(/\*/g, '')
                      .trim()
                );
              }

              if (afterText.split('**Key Steps:**')[0].trim()) {
                descriptionAfter = afterText.split('**Key Steps:**')[0].trim()
                  .replace(/\n+/g, ' ')
                  .replace(/\*\*/g, '')
                  .replace(/\*/g, '')
                  .replace(/^\s*-\s+/, '')
                  .trim();
              }
            } else {
              descriptionAfter = afterText
                .replace(/\n+/g, ' ')
                .replace(/\*\*/g, '')
                .replace(/\*/g, '')
                .replace(/^\s*-\s+/, '')
                .trim();
            }
          }
        }
      }

      // Generate stable ID based on title (like features do)
      const stableId = currentTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-');

      diagrams.push({
        id: stableId,
        title: currentTitle,
        descriptionBefore: descriptionBefore,
        descriptionAfter: descriptionAfter,
        keySteps: keySteps,
        code: match[1].trim()
      });
    }

    return diagrams;
  };

  // Configuration for process flow version management
  const processFlowConfig = {
    // Determine if a message contains process flows
    isRelevantMessage: (msg) => {
      const content = msg.content || '';
      return content.includes('```mermaid');
    },

    // Parse diagrams from content — prefer backend parsed_content
    parseContent: (content, msg) => {
      if (msg?.parsed_content?.sections?.length) {
        return msg.parsed_content.sections;
      }
      return extractMermaidDiagrams(content);
    },

    // Extract version information from a message
    // Note: The id will be set by useVersionManagement based on messageIndex
    // We return null for id here to let the parent hook set it properly
    extractVersions: (msg, parsedDiagrams, versionNumber, isPreview = false) => {
      return {
        id: null, // Will be overwritten by useVersionManagement with messageIndex-based ID
        timestamp: msg.createdAt || new Date().toISOString(),
        diagrams: parsedDiagrams,
        content: msg.content,
        versionNumber: versionNumber,
        isPreview: isPreview,
        label: isPreview ? 'Generating...' : undefined
      };
    },

    conversation,
    isStreaming,
    streamingContent,
    isLocalUpdateRef
  };

  // Use the fixed version management hook (V1)
  const versionData = useVersionManagementV1(processFlowConfig);

  // Memoize the currentVersion to prevent unnecessary re-renders
  // Only recreate when the actual version data changes
  const currentVersion = useMemo(() => {
    if (!versionData.currentVersion) return null;
    return {
      ...versionData.currentVersion,
      diagrams: versionData.currentVersion.parsedContent || versionData.currentVersion.diagrams
    };
  }, [versionData.currentVersion]);

  // Map the generic version data to process flow-specific structure
  return {
    versions: versionData.versions,
    currentVersion,
    currentVersionIndex: versionData.currentVersionIndex,
    setCurrentVersionIndex: versionData.setCurrentVersionIndex,
    hasVersions: versionData.hasVersions,
    isLatestVersion: versionData.isLatestVersion,
    addVersion: versionData.addVersion,
    updateCurrentVersion: versionData.updateCurrentVersion
  };
};

export default useProcessFlowVersions;
